import { createBaseInputClass } from '@descope-ui/common/base-classes';
import {
  getComponentName,
  forwardAttrs,
} from '@descope-ui/common/components-helpers';
import { compose } from '@descope-ui/common/utils';
import {
  fetchCountries,
  fetchSubdivisions,
  fetchCities,
  fetchCitiesForCountry,
  fetchLabels,
} from '../../data/dataService';

export const componentName = getComponentName(
  'country-subdivision-city-field-internal',
);

// Sentinel used in #pendingValue to signal that programmatic item selection is in progress.
// Prevents cascade-clearing logic in input handlers from firing during programmatic selection.
const SELECTING = Symbol('selecting');

// --- Translation ---

const resolveTranslation = ({ name, translations = {} }, lang) => {
  if (!lang) return name;
  return translations[lang] || translations[lang.split('-')[0]] || name;
};

// --- HTML Escaping ---

const escapeHtml = (str) => {
  if (!str) return '';
  return String(str).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
};

// --- Item renderers ---

const countryItem = ({ name, iso2, iso3, translations, lang }) => {
  const displayName = resolveTranslation({ name, translations }, lang);
  const searchTerms = [displayName, iso2, iso3];
  if (displayName !== name) searchTerms.push(name);
  const escapedIso2 = escapeHtml(iso2);
  const escapedSearchTerms = escapeHtml(searchTerms.join(' '));
  const escapedLabel = escapeHtml(displayName);
  return `<div data-id="${escapedIso2}" data-name="${escapedSearchTerms}" data-label="${escapedLabel}">${escapedLabel}</div>`;
};

const subdivisionItem = ({ name, state_code, translations, lang }) => {
  const displayName = resolveTranslation({ name, translations }, lang);
  const searchTerms = [displayName, state_code];
  if (displayName !== name) searchTerms.push(name);
  const escapedStateCode = escapeHtml(state_code);
  const escapedSearchTerms = escapeHtml(searchTerms.join(' '));
  const escapedLabel = escapeHtml(displayName);
  return `<div data-id="${escapedStateCode}" data-name="${escapedSearchTerms}" data-label="${escapedLabel}">${escapedLabel}</div>`;
};

const cityItem = ({ name, translations, lang }) => {
  const displayName = resolveTranslation({ name, translations }, lang);
  const searchTerms = [displayName];
  if (displayName !== name) searchTerms.push(name);
  const escapedId = escapeHtml(name);
  const escapedSearchTerms = escapeHtml(searchTerms.join(' '));
  const escapedLabel = escapeHtml(displayName);
  return `<div data-id="${escapedId}" data-name="${escapedSearchTerms}" data-label="${escapedLabel}">${escapedLabel}</div>`;
};

const comboBoxHTML = (id) =>
  `<descope-combo-box id="${id}" item-label-path="data-name" item-value-path="data-id" allow-custom-value="false"></descope-combo-box>`;

// --- Base class ---

const BaseInputClass = createBaseInputClass({
  componentName,
  baseSelector: '',
});

const commonAttrs = ['disabled', 'readonly', 'size', 'bordered'];
const labelTypeAttrs = [
  'label-type',
  'country-label',
  'subdivision-label',
  'city-label',
];
const visibilityAttrs = ['hide-country', 'hide-subdivision', 'hide-city'];
const countryConfigAttrs = [
  'default-country',
  'allowed-countries',
  'pinned-countries',
];
const placeholderAttrs = [
  'country-placeholder',
  'subdivision-placeholder',
  'city-placeholder',
];
const observedAttrs = [
  ...commonAttrs,
  ...labelTypeAttrs,
  ...visibilityAttrs,
  ...countryConfigAttrs,
  ...placeholderAttrs,
  'required',
  'invalid',
  'data-source-base-url',
  'lang',
];

class RawCountrySubdivisionCityFieldInternal extends BaseInputClass {
  static get observedAttributes() {
    return [].concat(BaseInputClass.observedAttributes || [], observedAttrs);
  }

  #countryComboBox;
  #subdivisionComboBox;
  #cityComboBox;

  // Whether the subdivision/city field should be shown (data was found for selected country/subdivision)
  #subdivisionVisible = false;
  #cityVisible = false;

  // iso2 → country object (populated after countries are loaded)
  #countriesMap = new Map();
  #cachedCountries = null;
  #cachedSubdivisions = null;
  #cachedCities = null;
  #labels = null;

  // Value requested programmatically before data has loaded.
  // Applied incrementally as each cascade layer (countries → subdivisions → cities) loads.
  #pendingValue = null;

  constructor() {
    super();

    this.innerHTML = `
      <div class="wrapper">
        ${comboBoxHTML('country-combo')}
        ${comboBoxHTML('subdivision-combo')}
        ${comboBoxHTML('city-combo')}
      </div>
    `;

    this.#countryComboBox = this.querySelector('#country-combo');
    this.#subdivisionComboBox = this.querySelector('#subdivision-combo');
    this.#cityComboBox = this.querySelector('#city-combo');

    this.#setupValueTransform();
    this.#setupAttrForwarding();
  }

  // --- Attribute getters ---

  get #isHideCountry() {
    return this.getAttribute('hide-country') === 'true';
  }
  get #isHideSubdivision() {
    return this.getAttribute('hide-subdivision') === 'true';
  }
  get #isHideCity() {
    return this.getAttribute('hide-city') === 'true';
  }
  get #isNoDataSource() {
    return !this.#baseUrl;
  }
  get #baseUrl() {
    return this.getAttribute('data-source-base-url') || undefined;
  }
  get #lang() {
    const lang = this.getAttribute('lang') || undefined;
    if (!lang) return undefined;
    try {
      const [canonical] = Intl.getCanonicalLocales(lang);
      return canonical;
    } catch {
      return undefined; // invalid BCP47 tag — fall back to English
    }
  }
  get #defaultCountry() {
    return this.getAttribute('default-country')?.toUpperCase() || undefined;
  }
  get #subdivisionLabel() {
    return this.getAttribute('subdivision-label') || undefined;
  }
  get #subdivisionPlaceholder() {
    return this.getAttribute('subdivision-placeholder') || undefined;
  }

  get #allowedCountries() {
    return (this.getAttribute('allowed-countries') || '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
  }

  get #pinnedCountries() {
    return (this.getAttribute('pinned-countries') || '')
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
  }

  get #allCombos() {
    return [
      this.#countryComboBox,
      this.#subdivisionComboBox,
      this.#cityComboBox,
    ];
  }

  // --- Value ---

  get value() {
    const country = this.#isHideCountry
      ? this.#defaultCountry
      : this.#countryComboBox.value || undefined;

    const subdivision =
      !this.#isHideSubdivision && this.#subdivisionVisible
        ? this.#subdivisionComboBox.value || undefined
        : undefined;

    const city =
      !this.#isHideCity && this.#cityVisible
        ? this.#cityComboBox.value || undefined
        : undefined;

    return { country, subdivision, city };
  }

  set value(val) {
    if (!val) return;
    let parsed;
    try {
      parsed = typeof val === 'string' ? JSON.parse(val) : val;
    } catch {
      // Invalid JSON — silently ignore to prevent component crash
      return;
    }
    this.#pendingValue = { ...parsed };
    if (this.#baseUrl) this.#loadForPendingValue();
  }

  // --- Lifecycle ---

  init() {
    this.addEventListener('focus', (e) => {
      if (e.isTrusted) {
        const firstInvalidCombo = this.#allCombos.find(
          (combo) =>
            !combo.classList.contains(`${componentName}-hidden`) &&
            !combo.checkValidity(),
        );
        (firstInvalidCombo || this.#getFirstVisibleCombo())?.focus();
      }
    });

    super.init?.();

    this.#refreshState();
    this.#setupChangeListeners();
    this.#initData();
    this.#setCombosItemRenderers();
  }

  #setCombosItemRenderers() {
    this.#countryComboBox.renderItem = countryItem;
    this.#subdivisionComboBox.renderItem = subdivisionItem;
    this.#cityComboBox.renderItem = cityItem;
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (oldValue === newValue) return;

    if (attrName === 'required') {
      this.#updateRequiredOnCombos();
    } else if (
      visibilityAttrs.includes(attrName) ||
      attrName === 'data-source-base-url'
    ) {
      this.#refreshState();
      if (attrName === 'data-source-base-url') this.#initData();
    } else if (attrName === 'default-country') {
      const upper = newValue?.toUpperCase();
      if (upper && upper !== newValue) {
        this.setAttribute('default-country', upper);
        return;
      }
      if (upper && this.#countriesMap.size) {
        this.#onCountrySelected(upper); // countries already loaded — trigger immediately
      } else if (upper && this.#isHideCountry) {
        // hide-country mode: countries may not have been loaded yet (e.g. no default-country at init).
        // Kick off loading now; #loadCountries will call #onCountrySelected once done.
        this.#loadCountries();
      }
    } else if (attrName === 'lang') {
      this.#rerenderOnLangChange();
    } else if (attrName === 'invalid') {
      if (newValue === 'true') this.#handleInvalidCombos();
    } else if (
      attrName === 'allowed-countries' ||
      attrName === 'pinned-countries'
    ) {
      if (!this.#isHideCountry) {
        // Re-render from cache if available to avoid a race condition between two simultaneous
        // fetch calls (one from data-source-base-url, one from the filter attribute change).
        if (this.#cachedCountries) {
          this.#renderCountryItems(this.#cachedCountries);
        } else {
          this.#loadCountries();
        }
      }
    } else if (attrName === 'country-label') {
      this.#setOrRemoveAttr(this.#countryComboBox, 'label', newValue);
    } else if (attrName === 'subdivision-label') {
      this.#setOrRemoveAttr(this.#subdivisionComboBox, 'label', newValue);
    } else if (attrName === 'city-label') {
      this.#setOrRemoveAttr(this.#cityComboBox, 'label', newValue);
    }
  }

  // --- Validation ---

  getValidity() {
    if (!this.isRequired) return {};

    const countryMissing = !this.#isHideCountry && !this.#countryComboBox.value;
    const subdivisionMissing =
      !this.#isHideSubdivision &&
      (this.#isNoDataSource || this.#subdivisionVisible) &&
      !this.#subdivisionComboBox.value;
    const cityMissing =
      !this.#isHideCity &&
      (this.#isNoDataSource || this.#cityVisible) &&
      !this.#cityComboBox.value;

    return countryMissing || subdivisionMissing || cityMissing
      ? { valueMissing: true }
      : {};
  }

  // Show validation errors on the specific combos that are missing a value.
  // Iterate in reverse so reportValidity's focus() lands on the first invalid combo last.
  #handleInvalidCombos() {
    for (const combo of [...this.#allCombos].reverse()) {
      if (combo.classList.contains(`${componentName}-hidden`)) continue;
      if (!combo.checkValidity()) combo.reportValidity();
    }
  }

  // --- Setup ---

  #setupValueTransform() {
    // Display only the item name in the input, while data-name includes ISO codes for search
    [
      this.#countryComboBox,
      this.#subdivisionComboBox,
      this.#cityComboBox,
    ].forEach((combo) => {
      combo.customValueTransformFn = (val) => {
        const item = combo.baseElement?.items?.find(
          (i) => i['data-name'] === val,
        );
        return item?.getAttribute('data-label') ?? val;
      };
    });
  }

  #setupAttrForwarding() {
    // Forward common attrs and label-type to all combos
    this.#allCombos.forEach((combo) => {
      forwardAttrs(this, combo, {
        includeAttrs: [...commonAttrs, 'label-type'],
      });
    });

    // Forward default-country as default-value to let the combo box handle initial selection
    forwardAttrs(this, this.#countryComboBox, {
      includeAttrs: ['default-country'],
      mapAttrs: { 'default-country': 'default-value' },
    });

    // Forward per-field placeholders
    [
      [this.#countryComboBox, 'country-placeholder'],
      [this.#subdivisionComboBox, 'subdivision-placeholder'],
      [this.#cityComboBox, 'city-placeholder'],
    ].forEach(([combo, attr]) => {
      forwardAttrs(this, combo, {
        includeAttrs: [attr],
        mapAttrs: { [attr]: 'placeholder' },
      });
    });
  }

  #setupChangeListeners() {
    // descope-combo-box dispatches 'input' (not 'change') on selection
    this.#countryComboBox.addEventListener('input', () =>
      this.#handleCountryChange(),
    );
    this.#subdivisionComboBox.addEventListener('input', () =>
      this.#handleSubdivisionChange(),
    );
    this.#cityComboBox.addEventListener('input', () =>
      this.#dispatchInputEvent(),
    );

    this.handleFocusEventsDispatching(this.#allCombos);
  }

  #initData() {
    // baseUrl is forwarded by the outer component after this element is connected;
    // wait for the attributeChangedCallback('data-source-base-url') to re-trigger.
    if (!this.#baseUrl) return;

    if (this.#pendingValue) {
      this.#loadForPendingValue();
      return;
    }

    if (!this.#isHideCountry || this.#defaultCountry) {
      // Load countries to populate #countriesMap even when hide-country=true, so that
      // #onCountrySelected can apply the correct subdivision type label for the default country.
      this.#loadCountries();
    }
  }

  // --- Programmatic value loading (parallel fetch) ---

  async #loadForPendingValue() {
    const {
      country: pendingCountry,
      subdivision: pendingSubdivision,
      city: pendingCity,
    } = this.#pendingValue;

    const shouldLoadSubdivisions =
      !!pendingSubdivision && !this.#isHideSubdivision;
    const shouldLoadCities = !!pendingCity && !this.#isHideCity;

    const countriesPromise =
      this.#cachedCountries ?? fetchCountries(this.#baseUrl);
    const labelsPromise = this.#labels ?? fetchLabels(this.#baseUrl);
    const subdivisionsPromise =
      shouldLoadSubdivisions && pendingCountry
        ? fetchSubdivisions(pendingCountry, this.#baseUrl)
        : null;
    const citiesPromise =
      shouldLoadCities && pendingCountry
        ? shouldLoadSubdivisions && pendingSubdivision
          ? fetchCities(pendingCountry, pendingSubdivision, this.#baseUrl)
          : fetchCitiesForCountry(pendingCountry, this.#baseUrl)
        : null;

    // Fetch everything in parallel; null entries resolve immediately as null
    const [countries, labels, subdivisions, cities] = (
      await Promise.allSettled([
        countriesPromise,
        labelsPromise,
        subdivisionsPromise,
        citiesPromise,
      ])
    ).map((result) => (result.status === 'fulfilled' ? result.value : null));

    // Cancelled by a subsequent set value call or user interaction
    if (!this.#pendingValue) return;

    if (labels) this.#labels = labels;

    // Collect items to select — do this before rendering so we can batch-select below
    const toSelect = [];

    if (countries) {
      if (!this.#cachedCountries) {
        this.#cachedCountries = countries;
        this.#renderCountryItems(countries);
      }
      if (pendingCountry && !this.#isHideCountry)
        toSelect.push([this.#countryComboBox, pendingCountry]);
    }

    const countryObj = pendingCountry
      ? this.#countriesMap.get(pendingCountry)
      : null;
    if (countryObj?.subdivisionType)
      this.#applySubdivisionType(countryObj.subdivisionType);

    if (subdivisions) {
      this.#cachedSubdivisions = subdivisions;
      this.#subdivisionVisible = subdivisions.length > 0;
      if (subdivisions.length > 0) {
        this.#subdivisionComboBox.data = subdivisions.map((s) => ({
          ...s,
          lang: this.#lang,
        }));
        if (pendingSubdivision)
          toSelect.push([this.#subdivisionComboBox, pendingSubdivision]);
      }
    }

    if (cities) {
      this.#cityVisible = cities.length > 0;
      if (cities.length > 0) {
        this.#cachedCities = cities;
        this.#cityComboBox.data = cities.map((c) => ({
          ...c,
          lang: this.#lang,
        }));
        if (pendingCity) toSelect.push([this.#cityComboBox, pendingCity]);
      }
    }

    this.#refreshState();

    // Apply all selections in one batch. #pendingValue is set to SELECTING so that
    // input events fired by selectedItem= don't trigger cascade-clearing in change handlers.
    this.#pendingValue = SELECTING;
    setTimeout(() => {
      if (this.#pendingValue !== SELECTING) return; // cancelled by a new set value call
      for (const [combo, id] of toSelect) {
        const item = combo.items?.find((i) => i['data-id'] === id);
        if (item) combo.selectedItem = item;
      }
      this.#pendingValue = null;
    });
  }

  // --- Change handlers ---

  #handleCountryChange() {
    if (this.#pendingValue === SELECTING) return; // programmatic selection in progress
    this.#pendingValue = null; // cancel any in-flight fetch
    this.#resetSubdivision();
    this.#resetCity();
    this.#onCountrySelected(this.#countryComboBox.value);
    this.#dispatchInputEvent();
  }

  #handleSubdivisionChange() {
    if (this.#pendingValue === SELECTING) return; // programmatic selection in progress
    this.#pendingValue = null; // cancel any in-flight fetch
    const subdivisionCode = this.#subdivisionComboBox.value;
    this.#resetCity();

    if (!this.#isHideCity && subdivisionCode) {
      const countryIso2 = this.#countryComboBox.value;
      if (countryIso2) this.#loadCities(countryIso2, subdivisionCode);
    }
    this.#dispatchInputEvent();
  }

  // --- Country selection ---

  #onCountrySelected(countryIso2) {
    if (!countryIso2) return;

    const country = this.#countriesMap.get(countryIso2);
    if (!country) return;

    const { hasSubdivisions, subdivisionType } = country;

    if (!this.#isHideSubdivision && hasSubdivisions) {
      this.#applySubdivisionType(subdivisionType);
      this.#loadSubdivisions(countryIso2);
    } else if (!this.#isHideCity) {
      this.#loadCities(countryIso2);
    }
  }

  // Apply subdivisionType as label/placeholder, but only when not overridden by an explicit attribute.
  #applySubdivisionType(subdivisionType) {
    const label = this.#resolveSubdivisionTypeLabel(subdivisionType);
    if (!this.#subdivisionLabel)
      this.#setOrRemoveAttr(this.#subdivisionComboBox, 'label', label || null);
    if (!this.#subdivisionPlaceholder)
      this.#setOrRemoveAttr(
        this.#subdivisionComboBox,
        'placeholder',
        label || null,
      );
  }

  #resolveSubdivisionTypeLabel(subdivisionType) {
    const types = this.#labels?.subdivisionTypes;
    const entry = types?.[subdivisionType] ?? types?._fallback;
    if (!entry) return subdivisionType;
    const lang = this.#lang;
    return (
      (lang && (entry[lang] || entry[lang.split('-')[0]])) ||
      entry.en ||
      subdivisionType
    );
  }

  // --- Data loading ---

  async #loadLabels() {
    try {
      this.#labels = await fetchLabels(this.#baseUrl);
    } catch {
      // Labels are optional; fall back to raw subdivisionType name
    }
  }

  async #loadCountries() {
    if (!this.#baseUrl) return;
    this.#countryComboBox.setAttribute('loading', 'true');
    try {
      const [countries] = await Promise.all([
        fetchCountries(this.#baseUrl),
        this.#loadLabels(),
      ]);
      this.#cachedCountries = countries;
      this.#renderCountryItems(countries);
      // Skip defaultCountry if a programmatic set value is in flight
      if (!this.#pendingValue && this.#defaultCountry)
        this.#onCountrySelected(this.#defaultCountry);
    } catch (e) {
      console.error(`[${componentName}] Failed to load countries`, e);
    } finally {
      this.#countryComboBox.removeAttribute('loading');
    }
  }

  async #loadSubdivisions(countryIso2) {
    this.#setVisible(this.#subdivisionComboBox, true);
    this.#subdivisionComboBox.setAttribute('loading', 'true');
    try {
      const subdivisions = await fetchSubdivisions(countryIso2, this.#baseUrl);
      this.#cachedSubdivisions = subdivisions;
      this.#subdivisionVisible = subdivisions.length > 0;
      this.#refreshState();

      if (subdivisions.length > 0) {
        this.#subdivisionComboBox.data = subdivisions.map((s) => ({
          ...s,
          lang: this.#lang,
        }));
      } else if (!this.#isHideCity) {
        this.#loadCities(countryIso2);
      }
    } catch (e) {
      console.error(
        `[${componentName}] Failed to load subdivisions for ${countryIso2}`,
        e,
      );
      this.#subdivisionVisible = false;
      this.#applyVisibility();
    } finally {
      this.#subdivisionComboBox.removeAttribute('loading');
    }
  }

  // stateCode is optional: when omitted, loads all cities for the country
  async #loadCities(countryIso2, stateCode) {
    this.#cityComboBox.setAttribute('loading', 'true');
    try {
      const cities = await (stateCode
        ? fetchCities(countryIso2, stateCode, this.#baseUrl)
        : fetchCitiesForCountry(countryIso2, this.#baseUrl));
      this.#cityVisible = cities.length > 0;
      this.#refreshState();
      if (cities.length > 0) {
        this.#cachedCities = cities;
        this.#cityComboBox.data = cities.map((c) => ({
          ...c,
          lang: this.#lang,
        }));
      }
    } catch (e) {
      console.error(
        `[${componentName}] Failed to load cities for ${countryIso2}${stateCode ? `/${stateCode}` : ''}`,
        e,
      );
    } finally {
      this.#cityComboBox.removeAttribute('loading');
    }
  }

  #rerenderOnLangChange() {
    if (this.#cachedCountries) {
      const prevCountry = this.#countryComboBox.value;
      this.#renderCountryItems(this.#cachedCountries);
      if (prevCountry) this.#selectItemById(this.#countryComboBox, prevCountry);
    }
    if (this.#cachedSubdivisions && this.#subdivisionVisible) {
      const prevSubdivision = this.#subdivisionComboBox.value;
      this.#subdivisionComboBox.data = this.#cachedSubdivisions.map((s) => ({
        ...s,
        lang: this.#lang,
      }));
      if (prevSubdivision)
        this.#selectItemById(this.#subdivisionComboBox, prevSubdivision);
    }
    if (this.#cachedCities && this.#cityVisible) {
      const prevCity = this.#cityComboBox.value;
      this.#cityComboBox.data = this.#cachedCities.map((c) => ({
        ...c,
        lang: this.#lang,
      }));
      if (prevCity) this.#selectItemById(this.#cityComboBox, prevCity);
    }
    // Re-apply translated subdivision type label for the new lang
    const currentIso2 = this.#countryComboBox.value;
    if (currentIso2) {
      const country = this.#countriesMap.get(currentIso2);
      if (country?.subdivisionType)
        this.#applySubdivisionType(country.subdivisionType);
    }
  }

  // --- Country item rendering ---

  #renderCountryItems(countries) {
    const result = this.#applyCountryFilters(countries);
    this.#countryComboBox.data = result.map((c) => ({
      ...c,
      // Include the translated name in the data for search purposes, but not in the label which is rendered by renderItem
      lang: this.#lang,
    }));
    this.#countriesMap = new Map(countries.map((c) => [c.iso2, c]));
  }

  #applyCountryFilters(countries) {
    const allowed = this.#allowedCountries;
    const pinned = this.#pinnedCountries;

    let filtered = allowed.length
      ? countries.filter((c) => allowed.includes(c.iso2))
      : [...countries];

    filtered.sort((a, b) =>
      resolveTranslation(a, this.#lang).localeCompare(
        resolveTranslation(b, this.#lang),
        this.#lang,
      ),
    );

    if (!pinned.length) return filtered;

    const pinnedItems = pinned
      .map((iso2) => filtered.find((c) => c.iso2 === iso2))
      .filter(Boolean);
    const rest = filtered.filter((c) => !pinned.includes(c.iso2));
    return [...pinnedItems, ...rest];
  }

  // --- Visibility & required ---

  #applyVisibility() {
    this.#setVisible(this.#countryComboBox, !this.#isHideCountry);
    this.#setVisible(
      this.#subdivisionComboBox,
      !this.#isHideSubdivision &&
        (this.#isNoDataSource || this.#subdivisionVisible),
    );
    this.#setVisible(
      this.#cityComboBox,
      !this.#isHideCity && (this.#isNoDataSource || this.#cityVisible),
    );
  }

  #setVisible(el, visible) {
    el?.classList.toggle(`${componentName}-hidden`, !visible);
  }

  #updateRequiredOnCombos() {
    const required = this.hasAttribute('required') ? 'true' : null;
    this.#setOrRemoveAttr(
      this.#countryComboBox,
      'required',
      !this.#isHideCountry ? required : null,
    );
    this.#setOrRemoveAttr(
      this.#subdivisionComboBox,
      'required',
      !this.#isHideSubdivision &&
        (this.#isNoDataSource || this.#subdivisionVisible)
        ? required
        : null,
    );
    this.#setOrRemoveAttr(
      this.#cityComboBox,
      'required',
      !this.#isHideCity && (this.#isNoDataSource || this.#cityVisible)
        ? required
        : null,
    );
  }

  #refreshState() {
    this.#applyVisibility();
    this.#updateRequiredOnCombos();
    // Re-evaluate validity so that the form knows when newly-visible required fields become empty.
    // e.g. subdivision becomes required after a country with states is selected.
    this.setCustomValidity('');
  }

  // --- Reset helpers ---

  #resetSubdivision() {
    this.#subdivisionComboBox.value = '';
    this.#subdivisionComboBox.data = [];
    this.#subdivisionVisible = false;
    // Clear any dynamically set label/placeholder from subdivisionType
    if (!this.#subdivisionLabel)
      this.#subdivisionComboBox.removeAttribute('label');
    if (!this.#subdivisionPlaceholder)
      this.#subdivisionComboBox.removeAttribute('placeholder');
    this.#refreshState();
  }

  #resetCity() {
    this.#cityComboBox.value = '';
    this.#cityComboBox.data = [];
    this.#cachedCities = null;
    this.#cityVisible = false;
    this.#refreshState();
  }

  // --- Utility helpers ---

  #dispatchInputEvent() {
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  #setOrRemoveAttr(el, attr, value) {
    if (value) el.setAttribute(attr, value);
    else el.removeAttribute(attr);
  }

  #getFirstVisibleCombo() {
    return this.#allCombos.find(
      (combo) => !combo.classList.contains(`${componentName}-hidden`),
    );
  }

  #selectItemById(comboBox, id) {
    // Defer the lookup so MutationObserver has time to populate comboBox.items
    setTimeout(() => {
      const item = comboBox.items?.find((i) => i['data-id'] === id);
      if (item) comboBox.selectedItem = item;
    });
  }
}

export const CountrySubdivisionCityFieldInternal = compose()(
  RawCountrySubdivisionCityFieldInternal,
);
